// SPDX-License-Identifier: MIT
import { supabase } from '../auth/supabase';

const BUCKET = 'project-attachments';

// Extension-to-MIME hint set we can read natively via File.text().
// PDF / Word / images would need extra libraries (pdfjs-dist, mammoth.js,
// OCR) ; we keep them uploaded-without-extraction for now and surface that
// in the "Régénérer enrichi" UX so the user knows what's been ingested.
const TEXT_EXTENSIONS = ['.md', '.txt', '.json', '.csv', '.yml', '.yaml', '.html', '.htm', '.rtf'];

const MAX_TEXT_CHARS_PER_FILE = 50_000;

// Module-scoped context store : per-project text from extracted attachments.
// Lives in memory for the session ; refreshing the page clears it (acceptable
// for v1 — the manifest is persisted in DB, the context is ephemeral). Keyed
// by `<userId>/<projectId>` and stores ordered entries by filename.
interface ContextEntry { filename: string; text: string }
const contextStore = new Map<string, ContextEntry[]>();
function ctxKey(u: string, p: string): string { return `${u}/${p}`; }

export interface ProjectContextSnapshot {
  entries: ContextEntry[];
  totalChars: number;
}

// Returns extracted text for a file the browser can read natively (text-based
// formats). Returns null for binary / unsupported formats — the file still
// gets uploaded but isn't added to the Magnify regeneration context.
export async function extractAttachmentText(file: File): Promise<string | null> {
  const lower = file.name.toLowerCase();
  const ext = lower.slice(lower.lastIndexOf('.'));
  if (!TEXT_EXTENSIONS.includes(ext)) return null;
  try {
    const text = await file.text();
    return text.slice(0, MAX_TEXT_CHARS_PER_FILE);
  } catch {
    return null;
  }
}

// Extract + concatenate the text of an uploaded folder's files into one blob,
// each section prefixed with the file's (relative) path. Non-text files are
// skipped (extractAttachmentText returns null). Total capped so a huge folder
// can't bloat the bootstrap request body (rides the forgejo-bootstrap request).
const MAX_FOLDER_CHARS = 150_000;

export async function extractFolderMatiere(files: File[]): Promise<string> {
  const sections: string[] = [];
  let total = 0;
  for (const file of files) {
    const text = await extractAttachmentText(file);
    if (!text) continue;
    // webkitRelativePath is set for webkitdirectory inputs; fall back to name.
    const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    const section = `## ${path}\n\n${text}`;
    if (total + section.length > MAX_FOLDER_CHARS) {
      sections.push(section.slice(0, MAX_FOLDER_CHARS - total));
      break;
    }
    sections.push(section);
    total += section.length;
  }
  return sections.join('\n\n');
}

export function addToContext(userId: string, projectId: string, filename: string, text: string): void {
  const key = ctxKey(userId, projectId);
  const list = contextStore.get(key) ?? [];
  // De-dup by filename (re-upload same name replaces).
  const idx = list.findIndex((e) => e.filename === filename);
  if (idx >= 0) list[idx] = { filename, text };
  else list.push({ filename, text });
  contextStore.set(key, list);
}

export function removeFromContext(userId: string, projectId: string, filename: string): void {
  const key = ctxKey(userId, projectId);
  const list = contextStore.get(key);
  if (!list) return;
  const filtered = list.filter((e) => e.filename !== filename);
  if (filtered.length === 0) contextStore.delete(key);
  else contextStore.set(key, filtered);
}

export function getProjectContext(userId: string, projectId: string): ProjectContextSnapshot {
  const entries = contextStore.get(ctxKey(userId, projectId)) ?? [];
  const totalChars = entries.reduce((acc, e) => acc + e.text.length, 0);
  return { entries, totalChars };
}

// Build the prompt-friendly representation of the project's extracted
// attachments. One section per file, separated by `---`, capped to a soft
// total budget so the Magnify call stays within Claude's adaptive-thinking
// envelope. The edge function injects this string after the user's idea.
export function buildContextString(userId: string, projectId: string, maxTotalChars = 150_000): string {
  const { entries } = getProjectContext(userId, projectId);
  if (entries.length === 0) return '';
  const lines: string[] = [];
  let remaining = maxTotalChars;
  for (const e of entries) {
    if (remaining <= 200) break;
    const slice = e.text.slice(0, remaining - 100);
    lines.push(`--- ${e.filename} ---`);
    lines.push(slice);
    remaining -= slice.length + e.filename.length + 10;
  }
  return lines.join('\n');
}

// Storage path convention : `<user_id>/<project_id>/<filename>`. The RLS
// policies in supabase/migrations/2026-05-16T1700_create_project_attachments_bucket.sql
// gate operations on the first folder segment matching auth.uid(), so the
// SPA can pass any path under its own user_id prefix safely.
export interface AttachmentRow {
  // Full storage path (relative to bucket root).
  path: string;
  // Filename only (last segment of path) for display.
  name: string;
  size: number;
  mime: string;
  // Timestamp when the file was uploaded.
  created_at: string;
}

function pathFor(userId: string, projectId: string, filename: string): string {
  // Prefix the filename with a short random + timestamp segment to avoid
  // collisions when the user drops two files with the same name across
  // multiple sessions. Keeps the displayed name clean by stripping the
  // random later when rendering.
  const ts = Date.now().toString(36);
  const rnd = Math.random().toString(36).slice(2, 8);
  const safeName = filename.replace(/[^\w.\-]/g, '_');
  return `${userId}/${projectId}/${ts}-${rnd}-${safeName}`;
}

// Strips the random prefix from a stored path to recover the user-facing
// filename. Mirrors the prefix scheme of pathFor() — change one, change both.
export function displayNameFromPath(path: string): string {
  const last = path.split('/').pop() ?? path;
  const m = last.match(/^[a-z0-9]+-[a-z0-9]+-(.+)$/);
  return m ? m[1]! : last;
}

export async function uploadAttachment(
  userId: string,
  projectId: string,
  file: File,
): Promise<AttachmentRow> {
  const path = pathFor(userId, projectId, file.name);
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream' });
  if (error) throw error;
  return {
    path,
    name: file.name,
    size: file.size,
    mime: file.type || 'application/octet-stream',
    created_at: new Date().toISOString(),
  };
}

// Lists files at the project's storage path. Supabase returns metadata
// including size and mime via the `metadata` field on each entry.
export async function listAttachments(
  userId: string,
  projectId: string,
): Promise<AttachmentRow[]> {
  const folder = `${userId}/${projectId}`;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .list(folder, { limit: 100, sortBy: { column: 'created_at', order: 'desc' } });
  if (error) throw error;
  return (data ?? [])
    .filter((f) => f.name && !f.name.endsWith('/')) // skip pseudo-folders
    .map((f) => {
      const meta = (f.metadata ?? {}) as Record<string, unknown>;
      return {
        path: `${folder}/${f.name}`,
        name: displayNameFromPath(f.name),
        size: typeof meta.size === 'number' ? meta.size : 0,
        mime: typeof meta.mimetype === 'string' ? meta.mimetype : 'application/octet-stream',
        created_at: typeof f.created_at === 'string' ? f.created_at : new Date().toISOString(),
      };
    });
}

export async function deleteAttachment(path: string): Promise<void> {
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) throw error;
}

// Creates a short-lived signed URL for download / inline view. 1 hour TTL —
// enough for the user to click through and back, short enough that a leaked
// URL doesn't survive long.
export async function getSignedUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, 3600);
  if (error) throw error;
  return data.signedUrl;
}
